import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner,
  Badge,
  Table,
  Form,
  Modal,
  Alert,
  InputGroup,
  Pagination as BootstrapPagination,
} from "react-bootstrap";
import Swal from 'sweetalert2';
import {
  FaPlus,
  FaEdit,
  FaEye,
  FaTrash,
  FaBuilding,
  FaUsers,
  FaEnvelope,
  FaPhone,
  FaGlobe,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaIndustry,
  FaCheckCircle,
  FaTimes,
  FaUser,
  FaCheck,
  FaSearch,
  FaFilter,
  FaSort,
  FaUserPlus,
  FaUserMinus,
} from "react-icons/fa";
import { AppDispatch, RootState, useAppDispatch } from "../../store";
import { Icon } from "../../components/common/Icon";
import { useTranslation } from "react-i18next";
import companiesService, { Company, CompanyUpdate } from "../../features/companies/companiesService";
import usersService from "../../features/users/usersService";
import CompanyFormModal from "../companies/components/CompanyFormModal";
import Pagination from "../../components/common/Pagination";
import CompanyAvatar from "../../components/common/CompanyAvatar";
import { useTheme } from "../../context/ThemeContext";

const ClientCompaniesPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const [clientCompanies, setClientCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showAssignUsersModal, setShowAssignUsersModal] = useState(false);
  const [companyUsers, setCompanyUsers] = useState<any[]>([]);
  const [availableMarketingSpecialists, setAvailableMarketingSpecialists] = useState<any[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingSpecialists, setIsLoadingSpecialists] = useState(false);
  const [selectedUsersToAssign, setSelectedUsersToAssign] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  // Client companies'leri yükle
  useEffect(() => {
    loadClientCompanies();
  }, []);

  const loadClientCompanies = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const companies = await companiesService.getMyClientCompanies();
      setClientCompanies(companies);
    } catch (err) {
      console.error("Error loading client companies:", err);
      setError("Failed to load client companies. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    loadClientCompanies();
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    setSelectedCompany(null);
    loadClientCompanies();
  };

  const handleModalHide = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedCompany(null);
    loadClientCompanies();
  };

  const handleViewCompany = (company: Company) => {
    setSelectedCompany(company);
    setShowDetailModal(true);
  };

  const handleEditCompany = (company: Company) => {
    setSelectedCompany(company);
    setShowEditModal(true);
  };

  const handleViewUsers = async (company: Company) => {
    try {
      setIsLoadingUsers(true);
      setSelectedCompany(company);
      const users = await companiesService.getCompanyUsers(company.id);
      setCompanyUsers(users);
      setShowUsersModal(true);
    } catch (err) {
      console.error("Error loading company users:", err);
      setError("Failed to load company users. Please try again.");
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleCloseUsersModal = () => {
    setShowUsersModal(false);
    setSelectedCompany(null);
    setCompanyUsers([]);
    setIsLoadingUsers(false);
  };

  const handleAssignUsers = async (company: Company) => {
    try {
      setIsLoadingSpecialists(true);
      setSelectedCompany(company);
      
      // Agency Owner'ın tüm şirketlerindeki marketing specialist'ları getir
      const allUsers = await usersService.getMyCompaniesUsers();
      
      // Sadece marketing_specialist rolündeki kullanıcıları filtrele
      const specialists = allUsers.filter(user => {
        // roles string array olarak geliyor, object array değil
        return user.roles && user.roles.includes("marketing_specialist");
      });
      
      setAvailableMarketingSpecialists(specialists);
      setSelectedUsersToAssign([]);
      setShowAssignUsersModal(true);
    } catch (err) {
      console.error("Error loading marketing specialists:", err);
      setError("Failed to load marketing specialists. Please try again.");
    } finally {
      setIsLoadingSpecialists(false);
    }
  };

  const handleCloseAssignUsersModal = () => {
    setShowAssignUsersModal(false);
    setSelectedUsersToAssign([]);
    setAvailableMarketingSpecialists([]);
    setIsLoadingSpecialists(false);
  };

  const handleUserSelectionChange = (userId: number, isChecked: boolean) => {
    if (isChecked) {
      setSelectedUsersToAssign([...selectedUsersToAssign, userId]);
    } else {
      setSelectedUsersToAssign(selectedUsersToAssign.filter(id => id !== userId));
    }
  };

  const handleAssignUsersSubmit = async () => {
    if (!selectedCompany || selectedUsersToAssign.length === 0) {
      return;
    }

    try {
      // Loading göster
      Swal.fire({
        title: 'Assigning Users...',
        text: 'Please wait while we assign the selected users to the company.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      
      const result = await usersService.assignUsersToCompany(selectedCompany.id, selectedUsersToAssign);
      
      // Assign Users modal'ını kapat
      handleCloseAssignUsersModal();
      
      // View users modal'ındaki verileri güncelle
      if (selectedCompany) {
        await handleViewUsers(selectedCompany);
      }
      
      // Başarı mesajı göster
      Swal.fire({
        title: 'Success!',
        text: `${selectedUsersToAssign.length} user(s) have been assigned to ${selectedCompany.company_name}.`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
      
      setError(null);
    } catch (err) {
      console.error("Error assigning users:", err);
      
      // Hata mesajı göster
      Swal.fire({
        title: 'Error!',
        text: 'Failed to assign users. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      
      setError("Failed to assign users. Please try again.");
    }
  };

  const handleRemoveUserFromCompany = async (userId: number) => {
    if (!selectedCompany) return;

    // Kullanıcı bilgilerini bul
    const userToRemove = companyUsers.find(user => user.user_id === userId);
    const userName = userToRemove?.full_name || userToRemove?.username || 'this user';

    // SweetAlert ile onay al
    const result = await Swal.fire({
      title: 'Remove User',
      text: `Are you sure you want to remove ${userName} from ${selectedCompany.company_name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove!',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      // Loading göster
      Swal.fire({
        title: 'Removing User...',
        text: 'Please wait while we remove the user from the company.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      
      // Backend API'sini kullanarak kullanıcıyı şirketten çıkar
      await companiesService.removeUsersFromCompany(selectedCompany.id, {
        user_ids: [userId]
      });
      
      // View users modal'ındaki verileri güncelle
      await handleViewUsers(selectedCompany);
      
      // Başarı mesajı göster
      Swal.fire({
        title: 'Success!',
        text: `${userName} has been removed from ${selectedCompany.company_name}.`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
      
      setError(null);
    } catch (err) {
      console.error("Error removing user from company:", err);
      
      // Hata mesajı göster
      Swal.fire({
        title: 'Error!',
        text: 'Failed to remove user from company. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      
      setError("Failed to remove user from company. Please try again.");
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <Badge bg="success" className="status-badge">
        <Icon icon={FaCheckCircle} className="me-1" />
        Active
      </Badge>
    ) : (
      <Badge bg="secondary" className="status-badge">
        <Icon icon={FaTimes} className="me-1" />
        Inactive
      </Badge>
    );
  };

  // Pagination
  const paginatedCompanies = clientCompanies.slice((page - 1) * limit, page * limit);
  const totalCount = clientCompanies.length;
  const totalPages = Math.ceil(totalCount / limit);
  const activeCount = clientCompanies.filter((c) => c.is_active).length;
  const inactiveCount = totalCount - activeCount;

  if (isLoading) {
    return (
      <div className="client-companies-loading">
        <div className="loading-container">
          <div className="loading-spinner">
            <Spinner animation="border" variant="primary" />
          </div>
          <p className="loading-text">Loading client companies...</p>
        </div>
      </div>
    );
  }

  return (
    <Container fluid className="py-4 companies-page">
      <style>{`
        .companies-page {
          background: ${isDark ? "#0f172a" : "#f8f9fa"};
        }
        .page-header {
          background: ${isDark ? "#1e293b" : "rgba(255, 255, 255, 0.95)"};
          border: 1px solid ${isDark ? "#334155" : "#e9ecef"};
          box-shadow: ${isDark ? "0 2px 10px rgba(0,0,0,0.4)" : "0 2px 6px rgba(0,0,0,0.06)"};
        }
        .header-icon {
          background: ${isDark ? "rgba(59,130,246,0.15)" : "rgba(102,126,234,0.12)"};
          color: ${isDark ? "#60a5fa" : "#4f46e5"};
        }
        .page-title { color: ${isDark ? "#e2e8f0" : "#22223b"}; }
        .page-subtitle { color: ${isDark ? "#94a3b8" : "#5c5c7a"}; }
        .content-card {
          background: ${isDark ? "#111827" : "rgba(255, 255, 255, 0.95)"};
          border: 1px solid ${isDark ? "#334155" : "transparent"};
          box-shadow: ${isDark ? "0 2px 14px rgba(0,0,0,0.5)" : "0 20px 40px rgba(0, 0, 0, 0.1)"};
        }
        .companies-table th {
          background: ${isDark ? "#0b1220" : "rgba(102,126,234,0.1)"};
          color: ${isDark ? "#e5e7eb" : "#333"};
        }
        .companies-table td { color: ${isDark ? "#e5e7eb" : "inherit"}; }
        .companies-table tr:hover {
          background: ${isDark ? "rgba(59,130,246,0.08)" : "rgba(102, 126, 234, 0.05)"};
        }
        .empty-minimal { color: ${isDark ? "#94a3b8" : "#6c757d"}; }
        .status-badge { color: #fff; }
      `}</style>
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-info">
            <div className="header-icon">
              <Icon icon={FaBuilding} />
            </div>
            <div className="header-text">
              <h1 className="page-title">{t("client_companies")}</h1>
              <p className="page-subtitle">
                {totalCount} {t("total")} • {activeCount} {t("active")}
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            className="add-button"
            onClick={() => setShowCreateModal(true)}
          >
            <Icon icon={FaPlus} className="me-2" />
            {t("add_new_client_company")}
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter and Status Badges */}
      <Card className="filter-card mb-3">
        <Card.Body className="p-3 d-flex flex-wrap align-items-center gap-3 justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <Badge bg="success" className="status-badge">
              {activeCount} {t("active")}
            </Badge>
            <Badge bg="secondary" className="status-badge">
              {inactiveCount} {t("inactive")}
            </Badge>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Form.Select
              size="sm"
              value={limit}
              onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
              style={{ width: 80 }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
          </div>
        </Card.Body>
      </Card>

      {/* Content */}
      <Card className="content-card">
        <Card.Body className="p-0">
          {paginatedCompanies.length === 0 ? (
            <div className="empty-minimal text-center">
              <div className="text-muted small mb-1">{t("no_client_companies")}</div>
              <div className="text-muted xsmall">{t("no_client_companies_desc")}</div>
            </div>
          ) : (
            <Table responsive className="companies-table mb-0">
              <thead>
                <tr>
                  <th style={{ width: 60 }}></th>
                  <th>{t("company_name")}</th>
                  <th>{t("industry")}</th>
                  <th>{t("contact")}</th>
                  <th>{t("location")}</th>
                  <th>{t("status")}</th>
                  <th style={{ width: 120 }}>{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCompanies.map((company) => (
                  <tr key={company.id}>
                                          <td>
                        <CompanyAvatar
                          companyName={company.company_name}
                          logoUrl={company.logo_url}
                          size="md"
                        />
                      </td>
                    <td>
                      <div className="fw-semibold">{company.company_name}</div>
                      {company.company_size && (
                        <div className="text-muted small">
                          <Icon icon={FaUsers} className="me-1" />
                          {company.company_size}
                        </div>
                      )}
                    </td>
                    <td>
                      {company.industry && (
                        <span className="text-muted">
                          <Icon icon={FaIndustry} className="me-1" />
                          {company.industry}
                        </span>
                      )}
                    </td>
                    <td>
                      {company.email && (
                        <div className="small">
                          <Icon icon={FaEnvelope} className="me-1" />
                          <a href={`mailto:${company.email}`}>{company.email}</a>
                        </div>
                      )}
                      {company.phone && (
                        <div className="small">
                          <Icon icon={FaPhone} className="me-1" />
                          <a href={`tel:${company.phone}`}>{company.phone}</a>
                        </div>
                      )}
                    </td>
                    <td>
                      {company.city && company.country && (
                        <span className="text-muted small">
                          <Icon icon={FaMapMarkerAlt} className="me-1" />
                          {company.city}, {company.country}
                        </span>
                      )}
                    </td>
                    <td>{getStatusBadge(company.is_active)}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleViewCompany(company)}
                          title={t("view_details")}
                        >
                          <Icon icon={FaEye} />
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleEditCompany(company)}
                          title={t("edit_company")}
                        >
                          <Icon icon={FaEdit} />
                        </Button>
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => handleViewUsers(company)}
                          title="View & Manage Users"
                        >
                          <Icon icon={FaUsers} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Pagination */}
      {paginatedCompanies.length > 0 && totalPages > 1 && (
        <div className="d-flex justify-content-end mt-3">
          <Pagination
            currentPage={page}
            totalCount={totalPages}
            pageSize={limit}
            onPageChange={setPage}
          />
        </div>
      )}

      {/* Create/Edit/Detail Modals (aynı şekilde bırakıyoruz) */}
      <CompanyFormModal
        show={showCreateModal}
        onHide={handleModalHide}
        initialCompanyType="client"
      />
      {selectedCompany && (
        <CompanyFormModal
          show={showEditModal}
          onHide={handleModalHide}
          company={selectedCompany}
        />
      )}

      {/* Company Users Management Modal */}
      <Modal
        show={showUsersModal}
        onHide={handleCloseUsersModal}
        size="xl"
        centered
        className="users-management-modal"
      >
        <Modal.Header closeButton className="modal-header">
          <Modal.Title className="d-flex align-items-center">
            <div className="modal-icon me-3">
              <Icon icon={FaUsers} />
            </div>
            <div>
              <h5 className="mb-0">Manage Users</h5>
              <p className="text-muted mb-0 small">{selectedCompany?.company_name}</p>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {isLoadingUsers ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading users...</p>
            </div>
          ) : (
            <div>
              {/* Current Users Section */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h6 className="mb-1 fw-semibold">Current Marketing Specialists</h6>
                    <p className="text-muted small mb-0">
                      Manage users assigned to this client company
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    className="assign-button"
                    onClick={() => {
                      if (selectedCompany) {
                        handleAssignUsers(selectedCompany);
                      }
                    }}
                  >
                    <Icon icon={FaPlus} className="me-2" />
                    Assign Users
                  </Button>
                </div>
                
                {(() => {
                  const filteredUsers = companyUsers.filter((user) => {
                    const userRoles = user.roles || [];
                    // roles object array olarak geliyor, key alanını kontrol et
                    return userRoles.some((role: any) => role.key === "marketing_specialist");
                  });
                  return filteredUsers.length === 0;
                })() ? (
                  <div className="text-center py-5">
                    <div className="empty-state-icon mb-4">
                      <Icon icon={FaUsers} size="3x" />
                    </div>
                    <h5 className="mb-2">No Marketing Specialists</h5>
                    <p className="text-muted mb-4">
                      No marketing specialists are currently assigned to this company.
                    </p>
                    <Button
                      variant="primary"
                      size="lg"
                      className="assign-button"
                      onClick={() => {
                        if (selectedCompany) {
                          handleAssignUsers(selectedCompany);
                        }
                      }}
                    >
                      <Icon icon={FaPlus} className="me-2" />
                      Assign Marketing Specialists
                    </Button>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-hover users-table">
                      <thead className="table-light">
                        <tr>
                          <th>User</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th style={{ width: 100 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companyUsers
                          .filter((user) => {
                            const userRoles = user.roles || [];
                            // roles object array olarak geliyor, key alanını kontrol et
                            return userRoles.some((role: any) => role.key === "marketing_specialist");
                          })
                          .map((user) => (
                            <tr key={user.user_id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="user-avatar me-3">
                                    <Icon icon={FaUser} />
                                  </div>
                                  <div>
                                    <div className="fw-semibold">{user.full_name || user.username}</div>
                                    <div className="text-muted small">@{user.username}</div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <a href={`mailto:${user.email}`} className="text-decoration-none">
                                  {user.email}
                                </a>
                              </td>
                              <td>
                                <Badge bg="info" className="px-2 py-1">
                                  Marketing Specialist
                                </Badge>
                              </td>
                              <td>
                                <Badge 
                                  bg={user.status === "active" ? "success" : "secondary"}
                                  className="px-2 py-1"
                                >
                                  {user.status === "active" ? "Active" : "Inactive"}
                                </Badge>
                              </td>
                              <td>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  className="action-button"
                                  onClick={() => handleRemoveUserFromCompany(user.user_id)}
                                  title="Remove from company"
                                >
                                  <Icon icon={FaTrash} />
                                </Button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="modal-footer">
          <Button
            variant="secondary"
            onClick={handleCloseUsersModal}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Assign Users Modal */}
      <Modal
        show={showAssignUsersModal}
        onHide={handleCloseAssignUsersModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="modal-header">
          <Modal.Title>
            <Icon icon={FaUsers} className="me-2" />
            Assign Marketing Specialists - {selectedCompany?.company_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoadingSpecialists ? (
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading marketing specialists...</p>
            </div>
          ) : availableMarketingSpecialists.length === 0 ? (
            <div className="text-center">
              <Icon icon={FaUsers} size="2x" className="text-muted mb-3" />
              <p>No marketing specialists available in your agency.</p>
              <p className="text-muted small">Create marketing specialists in My Team page first.</p>
            </div>
          ) : (
            <div>
              <p className="mb-3">Select marketing specialists to assign to this client company:</p>
              <div className="table-responsive">
                <Table className="table-hover assign-users-table">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 80, textAlign: 'center' }}>
                        <div className="d-flex justify-content-center align-items-center">
                          <Form.Check
                            type="checkbox"
                            className="select-all-checkbox"
                            checked={selectedUsersToAssign.length === availableMarketingSpecialists.length && availableMarketingSpecialists.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUsersToAssign(availableMarketingSpecialists.map(u => u.id));
                              } else {
                                setSelectedUsersToAssign([]);
                              }
                            }}
                          />
                        </div>
                      </th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Current Assignment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableMarketingSpecialists.map((specialist) => {
                      // Kullanıcının bu şirkette zaten atanmış olup olmadığını kontrol et
                      const isAlreadyAssigned = companyUsers.some((user: any) => user.user_id === specialist.id);
                      
                      return (
                        <tr key={specialist.id} className={isAlreadyAssigned ? "table-secondary" : ""}>
                          <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                            <div className="d-flex justify-content-center align-items-center">
                              <Form.Check
                                type="checkbox"
                                className="user-checkbox"
                                checked={selectedUsersToAssign.includes(specialist.id)}
                                onChange={(e) => handleUserSelectionChange(specialist.id, e.target.checked)}
                                disabled={isAlreadyAssigned}
                              />
                            </div>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="user-avatar me-3">
                                <Icon icon={FaUser} />
                              </div>
                              <div>
                                <div className="fw-semibold">{specialist.full_name || specialist.username}</div>
                                <div className="text-muted small">@{specialist.username}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <a href={`mailto:${specialist.email}`} className="text-decoration-none">
                              {specialist.email}
                            </a>
                          </td>
                          <td>
                            <Badge 
                              bg={specialist.status === "active" ? "success" : "secondary"}
                              className="px-2 py-1"
                            >
                              {specialist.status === "active" ? "Active" : "Inactive"}
                            </Badge>
                          </td>
                          <td>
                            {isAlreadyAssigned ? (
                              <Badge bg="info" className="px-2 py-1">
                                <Icon icon={FaCheck} className="me-1" />
                                Already Assigned
                              </Badge>
                            ) : (
                              <span className="text-muted">
                                <Icon icon={FaPlus} className="me-1" />
                                Not Assigned
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="modal-footer">
          <Button
            variant="secondary"
            onClick={handleCloseAssignUsersModal}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssignUsersSubmit}
            disabled={selectedUsersToAssign.length === 0}
          >
            Assign Selected ({selectedUsersToAssign.length})
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ClientCompaniesPage;

// CSS Styles
const styles = `
  .client-companies-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 20px 0;
  }

  .page-header {
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(20px);
    border-radius: 20px;
    margin-bottom: 30px;
    padding: 30px 0;
  }

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .header-info {
    display: flex;
    align-items: center;
  }

  .header-icon {
    background: rgba(102, 126, 234, 0.12);
    border-radius: 15px;
    padding: 15px;
    margin-right: 20px;
    color: #4f46e5;
    font-size: 24px;
  }

  .page-title {
    color: #22223b;
    margin: 0;
    font-weight: 700;
    font-size: 2rem;
    letter-spacing: -0.5px;
  }

  .page-subtitle {
    color: #5c5c7a;
    margin: 5px 0 0 0;
    font-size: 1rem;
  }

  .add-button {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    border-radius: 15px;
    padding: 12px 24px;
    font-weight: 600;
    box-shadow: 0 8px 16px rgba(102, 126, 234, 0.3);
    transition: all 0.3s ease;
  }

  .add-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(102, 126, 234, 0.4);
  }

  .content-card {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    border-radius: 20px;
    border: none;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }

  .companies-table {
    margin: 0;
  }

  .companies-table th {
    background: rgba(102, 126, 234, 0.1);
    border: none;
    padding: 20px 15px;
    font-weight: 600;
    color: #333;
  }

  .companies-table td {
    border: none;
    padding: 20px 15px;
  }

  .user-avatar {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 14px;
  }

  .table-hover tbody tr:hover {
    background-color: rgba(102, 126, 234, 0.05);
  }

  .table-light th {
    background-color: #f8f9fa;
    border-bottom: 2px solid #dee2e6;
    font-weight: 600;
    color: #495057;
  }

  .users-management-modal .modal-header-custom {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-bottom: none;
  }

  .users-management-modal .modal-header-custom .btn-close {
    filter: invert(1);
  }

  .modal-icon {
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
  }

  .assign-button {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    border-radius: 8px;
    padding: 8px 16px;
    font-weight: 500;
    transition: all 0.3s ease;
  }

  .assign-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  .action-button {
    border-radius: 6px;
    padding: 6px 10px;
    transition: all 0.2s ease;
  }

  .action-button:hover {
    transform: scale(1.05);
  }

  .empty-state-icon {
    color: #6c757d;
    opacity: 0.6;
  }

  .users-table th {
    background-color: #f8f9fa;
    border-bottom: 2px solid #dee2e6;
    font-weight: 600;
    color: #495057;
    padding: 15px 12px;
  }

  .users-table td {
    padding: 15px 12px;
    vertical-align: middle;
  }

  .modal-footer-custom {
    background-color: #f8f9fa;
    border-top: 1px solid #dee2e6;
  }
    vertical-align: middle;
  }

  .companies-table tr:hover {
    background: rgba(102, 126, 234, 0.05);
  }

  .company-info {
    display: flex;
    align-items: center;
  }

  .company-logo {
    width: 50px;
    height: 50px;
    border-radius: 12px;
    overflow: hidden;
    margin-right: 15px;
    background: #f8f9fa;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  .logo-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .company-name {
    font-weight: 600;
    color: #333;
    margin-bottom: 5px;
  }

  .company-size {
    font-size: 0.85rem;
    color: #666;
  }

  .industry-info {
    color: #666;
  }

  .contact-info {
    font-size: 0.9rem;
  }

  .contact-item {
    margin-bottom: 5px;
  }

  .contact-item a {
    color: #667eea;
    text-decoration: none;
  }

  .contact-item a:hover {
    text-decoration: underline;
  }

  .location-info {
    font-size: 0.9rem;
    color: #666;
  }

  .location-item {
    margin-bottom: 5px;
  }

  .status-badge {
    padding: 8px 12px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
  }

  .action-buttons {
    display: flex;
    gap: 5px;
  }

  .action-buttons .btn {
    border-radius: 10px;
    padding: 8px 12px;
    font-size: 0.85rem;
  }

  .empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #666;
  }

  .empty-minimal {
    padding: 24px;
    color: #6c757d;
  }

  .empty-icon {
    margin-bottom: 20px;
    color: #ccc;
  }

  .empty-state h4 {
    margin-bottom: 10px;
    color: #333;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
  }

  .loading-spinner {
    margin-bottom: 20px;
  }

  .loading-text {
    color: white;
    font-size: 1.1rem;
  }

  .company-detail {
    padding: 20px 0;
  }

  .company-header {
    display: flex;
    align-items: center;
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 1px solid #eee;
  }

  .company-logo-large {
    width: 80px;
    height: 80px;
    border-radius: 15px;
    overflow: hidden;
    margin-right: 20px;
    background: #f8f9fa;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  }

  .logo-image-large {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .company-info-large h3 {
    margin: 0 0 10px 0;
    color: #333;
  }

  .detail-section {
    margin-top: 15px;
  }

  .detail-item {
    margin-bottom: 10px;
    padding: 10px;
    background: #f8f9fa;
    border-radius: 8px;
  }

  .detail-item strong {
    color: #333;
  }

  .users-table {
    margin-top: 20px;
  }

  .users-table th {
    background: rgba(102, 126, 234, 0.1);
    border: none;
    padding: 15px;
    font-weight: 600;
  }

  .users-table td {
    border: none;
    padding: 15px;
    vertical-align: middle;
  }

  .user-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    margin-right: 15px;
  }

  .user-info {
    display: flex;
    align-items: center;
  }

  .user-name {
    font-weight: 600;
    color: #333;
  }

  .user-email {
    font-size: 0.85rem;
    color: #666;
  }

  /* Assign Users Modal Table Styles */
  .assign-users-table {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .assign-users-table .table-light {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  }

  .assign-users-table th {
    border: none;
    padding: 12px 16px;
    font-weight: 600;
    color: #495057;
    background: transparent;
  }

  .assign-users-table td {
    border: none;
    padding: 12px 16px;
    vertical-align: middle;
  }

  .assign-users-table tbody tr {
    transition: all 0.2s ease;
  }

  .assign-users-table tbody tr:hover {
    background-color: rgba(102, 126, 234, 0.05);
    transform: translateY(-1px);
  }

  .assign-users-table tbody tr.table-secondary {
    background-color: rgba(108, 117, 125, 0.1);
  }

  .assign-users-table tbody tr.table-secondary:hover {
    background-color: rgba(108, 117, 125, 0.15);
  }

  /* Checkbox Styles */
  .select-all-checkbox,
  .user-checkbox {
    transform: scale(1.2);
    margin: 0;
  }

  .select-all-checkbox .form-check-input,
  .user-checkbox .form-check-input {
    width: 18px;
    height: 18px;
    border: 2px solid #dee2e6;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .select-all-checkbox .form-check-input:checked,
  .user-checkbox .form-check-input:checked {
    background-color: #667eea;
    border-color: #667eea;
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
  }

  .select-all-checkbox .form-check-input:focus,
  .user-checkbox .form-check-input:focus {
    border-color: #667eea;
    border-radius: 4px;
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
  }

  .select-all-checkbox .form-check-input:disabled,
  .user-checkbox .form-check-input:disabled {
    background-color: #e9ecef;
    border-color: #dee2e6;
    cursor: not-allowed;
    opacity: 0.6;
  }

  .select-all-checkbox .form-check-input:hover:not(:disabled),
  .user-checkbox .form-check-input:hover:not(:disabled) {
    border-color: #667eea;
  }

  /* Modal Header and Footer Styles */
  .modal-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-bottom: none;
  }

  .modal-footer {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-top: none;
  }

  .modal-icon {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* SweetAlert Custom Styles */
  .swal2-popup {
    border-radius: 15px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  }

  .swal2-title {
    color: #333;
    font-weight: 600;
  }

  .swal2-content {
    color: #666;
  }

  .swal2-confirm {
    border-radius: 8px;
    font-weight: 600;
    padding: 12px 24px;
  }

  .swal2-cancel {
    border-radius: 8px;
    font-weight: 600;
    padding: 12px 24px;
  }

  .swal2-success {
    border-color: #28a745;
  }

  .swal2-error {
    border-color: #dc3545;
  }

  .swal2-warning {
    border-color: #ffc107;
  }
`;

// Add styles to document
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
} 